import { ApolloError } from '@apollo/client';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { transformSearchCatalogResponse } from '@/experiences/sales-portal/domain/order-guides/blocs/transformers/search-catalog-transformer';
import { useCallback, useRef, useState } from 'react';
import { useGeneratedSearchCatalogForOrderGuideLazyQuery } from '@/experiences/sales-portal/domain/order-guides/blocs/documents/search-catalog-for-order-guide.generated';

interface UseSearchCatalogForOrderGuideLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseSearchCatalogForOrderGuideLoaderResult {
  /** Drop any in-flight query and clear results/error (call on close). */
  cancelSearch: () => void;
  error: ApolloError | null;
  loading: boolean;
  results: OrderGuideItem[];
  search: (searchTerm: string) => Promise<void>;
}

/**
 * Drives the "Add Items" catalog search. Tracks the latest term in a ref and
 * applies a response only while it's still current, so a slow earlier query
 * never clobbers a newer one. Empty/whitespace clears results without a request.
 */
export const useSearchCatalogForOrderGuideLoader = ({
  restaurantUUID,
  supplierUUID,
}: UseSearchCatalogForOrderGuideLoaderParams): UseSearchCatalogForOrderGuideLoaderResult => {
  const [results, setResults] = useState<OrderGuideItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApolloError | null>(null);
  const latestQueryRef = useRef<string>('');

  const runSearch = useGeneratedSearchCatalogForOrderGuideLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const search = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        latestQueryRef.current = '';
        setResults([]);
        setError(null);
        setLoading(false);
        return;
      }

      latestQueryRef.current = searchTerm;
      setLoading(true);
      setError(null);

      try {
        const result = await runSearch({
          variables: { restaurantUUID, searchTerm, supplierUUID },
        });
        if (searchTerm !== latestQueryRef.current) {
          return;
        }
        setResults(transformSearchCatalogResponse(result.data));
        setLoading(false);
      } catch (caught) {
        if (searchTerm !== latestQueryRef.current) {
          return;
        }
        setError(caught as ApolloError);
        setResults([]);
        setLoading(false);
      }
    },
    [restaurantUUID, supplierUUID, runSearch],
  );

  const cancelSearch = useCallback(() => {
    latestQueryRef.current = '';
    setLoading(false);
    setResults([]);
    setError(null);
  }, []);

  return { cancelSearch, error, loading, results, search };
};
